import {
  ActionIcon,
  Flex,
  Grid,
  Loader,
  Paper,
  ScrollArea,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

import type { CandidatePickCard, CandidatePickVacancyMeta } from '../../types';

import { CandidateMatchAssessment } from './candidate-match-assessment';
import { CandidatePickEmptyState } from './CandidatePickEmptyState';
import { CandidatePickListButton } from './candidate-pick-list-button';
import { CandidatePickListView } from './candidate-pick-list-view';
import { CandidatePickVacancyRow } from './CandidatePickVacancyRow';
import { CandidatePickProfileHeader } from './candidate-pick-profile-header';
import classes from './candidate-pick-drawer.module.scss';

type CandidatePickDrawerBodyProps = CandidatePickVacancyMeta & {
  candidates: CandidatePickCard[];
  currentIndex: number;
  listViewOpen: boolean;
  onIndexChange: (index: number) => void;
  onListViewOpen: () => void;
  onListViewClose: () => void;
  onListCandidateSelect: (index: number) => void;
  isLoading: boolean;
  isError: boolean;
};

export const CandidatePickDrawerBody = ({
  candidates,
  currentIndex,
  listViewOpen,
  onIndexChange,
  onListViewOpen,
  onListViewClose,
  onListCandidateSelect,
  vacancyTitle,
  vacantPlaces,
  isLoading,
  isError,
}: CandidatePickDrawerBodyProps) => {
  const candidate = candidates[currentIndex];
  const total = candidates.length;
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (isLoading) {
    return (
      <Flex flex={1} align='center' justify='center' miw={0} mih={200}>
        <Loader color='primary' aria-label='Loading candidate details' />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex flex={1} align='center' justify='center' miw={0} mih={200}>
        <Text size='sm' c='red.7' role='alert'>
          Не удалось загрузить рекомендованных кандидатов
        </Text>
      </Flex>
    );
  }

  if (!candidate) {
    return (
      <CandidatePickEmptyState
        vacancyTitle={vacancyTitle}
        vacantPlaces={vacantPlaces}
      />
    );
  }

  if (listViewOpen) {
    return (
      <CandidatePickListView
        candidates={candidates}
        currentIndex={currentIndex}
        vacantPlaces={vacantPlaces}
        onBack={onListViewClose}
        onSelect={onListCandidateSelect}
      />
    );
  }

  return (
    <Flex direction='column' gap={24} flex={1} miw={0} mih={0} w='100%'>
      {!isMobile && (
        <CandidatePickVacancyRow
          vacancyTitle={vacancyTitle}
          vacantPlaces={vacantPlaces}
        />
      )}

      <Paper
        bg={{ base: 'white', sm: 'primary.0' }}
        radius={16}
        bd='1px solid transparent'
        flex={1}
        mih={0}
        w='100%'
        display='flex'
        style={{ flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
      >
        <ScrollArea flex={1} mih={0} type='never' scrollbars='y'>
          <Flex
            direction='column'
            gap={8}
            px={24}
            pt={24}
            pb={16}
            className={classes.profileContent}
          >
            <CandidatePickProfileHeader
              key={candidate.candidateId}
              photoUrl={candidate.photoUrl}
              displayName={candidate.displayName}
              age={candidate.age}
            />

            <CandidateMatchAssessment
              matchPercent={candidate.matchPercent}
              analysis={candidate.analysis}
              resetKey={currentIndex}
            />

            <Flex
              direction='column'
              gap={8}
              py={16}
              className={classes.candidateSummary}
            >
              <Text size='sm' c='tertiary.7'>
                Опыт работы и навыки
              </Text>
              <Text size='md' c='primary.7'>
                {candidate.summary}
              </Text>
            </Flex>

            <Grid pt={16} gutter={{ base: 12, sm: 24 }}>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Flex
                  direction={{ base: 'row', sm: 'column' }}
                  justify='space-between'
                  gap={8}
                >
                  <Text size='sm' c='tertiary.7'>
                    Готов выйти на работу
                  </Text>
                  <Text size='md' c='primary.7'>
                    {candidate.readyToWorkDate}
                  </Text>
                </Flex>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Flex
                  direction={{ base: 'row', sm: 'column' }}
                  justify='space-between'
                  gap={8}
                >
                  <Text size='sm' c='tertiary.7'>
                    Уровень дохода
                  </Text>
                  <Text size='md' c='primary.7'>
                    {candidate.salary}
                  </Text>
                </Flex>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Flex
                  direction={{ base: 'row', sm: 'column' }}
                  justify='space-between'
                  gap={8}
                >
                  <Text size='sm' c='tertiary.7'>
                    Номер телефона
                  </Text>
                  <Text size='md' c='primary.7'>
                    {candidate.phone}
                  </Text>
                </Flex>
              </Grid.Col>
            </Grid>
          </Flex>
        </ScrollArea>

        <Flex
          bg='white'
          p={24}
          gap={8}
          align='center'
          className={classes.footer}
          style={{
            borderRadius: 16,
            boxShadow: '0 -20px 25px -5px rgba(0, 0, 0, 0.05)',
          }}
        >
          <ActionIcon
            type='button'
            variant='outline'
            color='gray'
            size={38}
            disabled={currentIndex <= 0}
            aria-label='Предыдущий кандидат'
            style={{ borderRadius: '50%' }}
            onClick={() => onIndexChange(currentIndex - 1)}
          >
            <IconChevronLeft size={20} />
          </ActionIcon>
          <Text
            size='sm'
            fw={600}
            c='primary.7'
            className={classes.footerCounter}
          >
            {currentIndex + 1} / {total}
          </Text>
          <ActionIcon
            type='button'
            variant='outline'
            color='gray'
            size={38}
            disabled={currentIndex >= total - 1}
            aria-label='Следующий кандидат'
            style={{ borderRadius: '50%' }}
            onClick={() => onIndexChange(currentIndex + 1)}
          >
            <IconChevronRight size={20} />
          </ActionIcon>
          <CandidatePickListButton onClick={onListViewOpen} />
        </Flex>
      </Paper>
    </Flex>
  );
};
